import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function PageBanner({ page = 'all' }) {
  const [banners, setBanners] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios.get(`${API_URL}/api/page-extras/public/${page}`, { headers })
      .then(r => setBanners(r.data?.banners || []))
      .catch(() => {});
  }, [page]);

  const visible = banners.filter(b => !dismissed.has(b.id));
  if (visible.length === 0) return null;

  return (
    <>
      {visible.map(b => (
        <div key={b.id} className="relative text-center py-2 px-4"
          style={{ backgroundColor: b.bg_color || '#1e3a5f', color: b.text_color || '#fff' }}>
          <span className="font-semibold text-sm">{b.title}</span>
          {b.content && <span className="ml-2 text-sm opacity-90">{b.content}</span>}
          {b.button_text && b.button_link && (
            <Link to={b.button_link} className="ml-3 underline font-bold text-sm">{b.button_text}</Link>
          )}
          <button onClick={() => setDismissed(prev => new Set([...prev, b.id]))}
            className="absolute right-2 top-1/2 -translate-y-1/2 opacity-70 hover:opacity-100">
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
    </>
  );
}

export function PagePopup({ page = 'all' }) {
  const [popup, setPopup] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios.get(`${API_URL}/api/page-extras/public/${page}`, { headers })
      .then(r => {
        const popups = r.data?.popups || [];
        if (popups.length > 0) {
          const dismissedKey = `popup_dismissed_${popups[0].id}`;
          if (!sessionStorage.getItem(dismissedKey)) {
            setPopup(popups[0]);
            setTimeout(() => setShow(true), 2000);
          }
        }
      }).catch(() => {});
  }, [page]);

  if (!show || !popup) return null;

  const dismiss = () => {
    setShow(false);
    sessionStorage.setItem(`popup_dismissed_${popup.id}`, 'true');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={dismiss}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 relative animate-in fade-in zoom-in-95" onClick={e => e.stopPropagation()}>
        <button onClick={dismiss} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600">
          <X className="h-5 w-5" />
        </button>
        {popup.image_url && <img src={popup.image_url} alt="" className="w-full h-40 object-cover rounded-lg mb-4" />}
        <h3 className="font-bold text-xl text-gray-900 mb-2">{popup.title}</h3>
        <p className="text-gray-600 mb-4">{popup.content}</p>
        {popup.button_text && popup.button_link && (
          <Link to={popup.button_link} onClick={dismiss}
            className="block w-full text-center bg-blue-900 text-white py-2.5 rounded-lg font-bold hover:bg-blue-800 transition-colors">
            {popup.button_text}
          </Link>
        )}
      </div>
    </div>
  );
}

export function PageSections({ page = 'all', position = 'top' }) {
  const [sections, setSections] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios.get(`${API_URL}/api/page-extras/public/${page}`, { headers })
      .then(r => {
        const all = r.data?.sections || [];
        setSections(all.filter(s => (s.position || 'top') === position));
      })
      .catch(() => {});
  }, [page, position]);

  if (sections.length === 0) return null;

  const paddingMap = { sm: 'py-6', md: 'py-10', lg: 'py-14', xl: 'py-20' };
  const titleMap = { xl: 'text-xl', '2xl': 'text-2xl', '3xl': 'text-3xl', '4xl': 'text-4xl' };
  const borderMap = { none: '', subtle: 'border-t border-b', accent: 'border-l-4 border-blue-500', shadow: 'shadow-lg' };

  return (
    <>
      {sections.map(s => {
        const padding = paddingMap[s.section_padding] || 'py-14';
        const titleSize = titleMap[s.title_size] || 'text-3xl';
        const border = borderMap[s.border_style] || '';
        const textAlign = s.text_align === 'left' ? 'text-left' : s.text_align === 'right' ? 'text-right' : 'text-center';
        const bgStyle = s.bg_color ? { backgroundColor: s.bg_color } : {};
        const txtStyle = s.text_color ? { color: s.text_color } : {};
        const imgClass = s.image_style === 'circle' ? 'rounded-full w-48 h-48 object-cover mx-auto' 
          : s.image_style === 'rounded' ? 'rounded-2xl object-cover' 
          : s.image_style === 'contain' ? 'object-contain max-h-64 mx-auto' 
          : 'w-full h-48 object-cover rounded-xl';
        const isHorizontal = s.layout === 'image-left' || s.layout === 'image-right';

        return (
          <section key={s.id} className={`${padding} ${border} ${s.layout === 'card' ? '' : ''}`} style={bgStyle}>
            <div className="container-app">
              {s.layout === 'card' ? (
                <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-lg overflow-hidden" style={txtStyle}>
                  {s.image_url && <img src={s.image_url} alt="" className="w-full h-52 object-cover" />}
                  <div className={`p-8 ${textAlign}`}>
                    <h2 className={`font-heading ${titleSize} font-bold mb-4`}>{s.title}</h2>
                    <p className="text-lg mb-6 opacity-80">{s.content}</p>
                    {s.button_text && s.button_link && (
                      <Link to={s.button_link} className="inline-block bg-rsc-yellow text-rsc-navy px-8 py-3 rounded-full font-bold hover:bg-rsc-yellow-bright">{s.button_text}</Link>
                    )}
                  </div>
                </div>
              ) : isHorizontal ? (
                <div className={`flex flex-col md:flex-row items-center gap-8 max-w-5xl mx-auto ${s.layout === 'image-right' ? 'md:flex-row-reverse' : ''}`} style={txtStyle}>
                  {s.image_url && (
                    <div className="flex-1"><img src={s.image_url} alt="" className={imgClass} /></div>
                  )}
                  <div className={`flex-1 ${textAlign}`}>
                    <h2 className={`font-heading ${titleSize} font-bold mb-4`}>{s.title}</h2>
                    <p className="text-lg mb-6 opacity-80">{s.content}</p>
                    {s.button_text && s.button_link && (
                      <Link to={s.button_link} className="inline-block bg-rsc-yellow text-rsc-navy px-8 py-3 rounded-full font-bold hover:bg-rsc-yellow-bright">{s.button_text}</Link>
                    )}
                  </div>
                </div>
              ) : s.layout === 'full-width' ? (
                <div className={`relative ${textAlign}`} style={txtStyle}>
                  {s.image_url && (
                    <div className="absolute inset-0 z-0">
                      <img src={s.image_url} alt="" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/50" />
                    </div>
                  )}
                  <div className="relative z-10 max-w-3xl mx-auto py-8" style={s.image_url ? { color: '#fff' } : txtStyle}>
                    <h2 className={`font-heading ${titleSize} font-bold mb-4`}>{s.title}</h2>
                    <p className="text-lg mb-6 opacity-90">{s.content}</p>
                    {s.button_text && s.button_link && (
                      <Link to={s.button_link} className="inline-block bg-rsc-yellow text-rsc-navy px-8 py-3 rounded-full font-bold hover:bg-rsc-yellow-bright">{s.button_text}</Link>
                    )}
                  </div>
                </div>
              ) : (
                <div className={`max-w-3xl mx-auto ${textAlign}`} style={txtStyle}>
                  {s.image_url && <img src={s.image_url} alt="" className={`${imgClass} mb-6`} />}
                  <h2 className={`font-heading ${titleSize} font-bold mb-4`}>{s.title}</h2>
                  <p className="text-lg opacity-80 mb-6">{s.content}</p>
                  {s.button_text && s.button_link && (
                    <Link to={s.button_link} className="inline-block bg-rsc-yellow text-rsc-navy px-8 py-3 rounded-full font-bold hover:bg-rsc-yellow-bright">{s.button_text}</Link>
                  )}
                </div>
              )}
            </div>
          </section>
        );
      })}
    </>
  );
}
